import { getStockStatusText } from '@ngg/storefront-utils';
import type { Dictionary } from 'types';
import type { StockStatus } from 'types/product';
import type { VariantAttribute } from 'types/variant';

import { convertValueToString } from '@/utils/functions';

import { isSoonBackInStock } from '../product/isSoonBackInStock';

export const selectableStockStatuses: StockStatus[] = [
    'DeliveryItem',
    'StockItem',
    'OutOfStock',
    'SoldOut',
    'PendingSoldOut',
];

/**
 * What is k1, v1, k2, v2, v?
 * And why are we doing this convertValueToString and localeCompare stuff?
 * @todo We need better named variables. This does not say anything about what it does.
 */
export const optionSorting = (
    [k1, v1]: [string, VariantAttribute[]],
    [k2, v2]: [string, VariantAttribute[]],
) => {
    const price =
        Math.min(...v1.map((v) => v.price.price)) -
        Math.min(...v2.map((v) => v.price.price));
    const stock =
        Number(...v2.map((v) => v.stock)) - Number(...v1.map((v) => v.stock));
    const text = convertValueToString(k1).localeCompare(
        convertValueToString(k2),
    );
    return stock || price || text;
};

/**
 * What is k1, v1, k2, v2, v?
 * And why are we doing this convertValueToString and localeCompare stuff?
 * @todo We need better named variables. This does not say anything about what it does.
 */
export const optionSortingAlwaysCheapestFirst = (
    [k1, v1]: [string, VariantAttribute[]],
    [k2, v2]: [string, VariantAttribute[]],
) => {
    const price =
        Math.min(...v1.map((v) => v.price.price)) -
        Math.min(...v2.map((v) => v.price.price));
    const text = convertValueToString(k1).localeCompare(
        convertValueToString(k2),
    );
    return price || text;
};

export const hasStockItemStatus = (attrs: VariantAttribute[] | undefined) =>
    attrs &&
    attrs.some(
        (x) =>
            x.stockStatus === 'StockItem' || x.stockStatus === 'PendingSoldOut',
    );

export const hasSoonBackInStockStatus = (
    attrs: VariantAttribute[] | undefined,
) =>
    attrs &&
    attrs.some(({ stockStatus, nextDeliveryDate }) =>
        isSoonBackInStock(stockStatus, nextDeliveryDate),
    );

const allHaveSoldOutStatus = (attrs: VariantAttribute[] | undefined) =>
    attrs && attrs.every((attr) => attr.stockStatus === 'SoldOut');

export const showStatus = (
    attrs: VariantAttribute[] | undefined,
    dictionary: Dictionary,
) => {
    if (!attrs?.length) return null;

    if (hasSoonBackInStockStatus(attrs)) {
        const { nextDeliveryDate, stockStatus } =
            attrs.find(({ stockStatus, nextDeliveryDate }) =>
                isSoonBackInStock(stockStatus, nextDeliveryDate),
            ) || {};

        return getStockStatusText({
            dictionary,
            isShortened: true,
            stockStatus: stockStatus!,
            nextDeliveryDate: nextDeliveryDate,
        });
    }

    if (hasStockItemStatus(attrs)) {
        return getStockStatusText({ stockStatus: 'StockItem', dictionary });
    }

    /** Hide delivery items for now
    if (attrs.every((attr) => attr.stockStatus === 'DeliveryItem')) {
        return getStockStatusText({ stockStatus: 'DeliveryItem', dictionary });
    } */

    if (allHaveSoldOutStatus(attrs)) {
        return getStockStatusText({ stockStatus: 'SoldOut', dictionary });
    }

    return null;
};
